import {
  useGetAllEncashmentByBatchQuery,
  type EncashmentRecord,
} from '@/app/features/employee/emp-payroll';
import CustomTable from '@/components/CustomTable';
import StatusTag from '@/components/StatusTag';
import { FILTER_KEYS } from '@/constants/keys.constants';
import { useAppFilters } from '@/hooks';
import useDiscloser from '@/hooks/useDiscloser';
import { LockIcon, PencilEdit02Icon } from '@/icons';
import DashboardLayout from '@/layouts/DashboardLayout';
import type { StatusOption } from '@/types/global.types';
import { Button, Tooltip, type TableProps } from 'antd';
import { useParams } from 'react-router';
import PayrollPageHeader from '../shared/PayrollPageHeader';
import TotalAmount from '../shared/TotalAmount';
import { PAYROLL_PATH } from '../shared/payroll.constants';
import EditEncashmentRecord from './EditEncashmentRecord';
import EncashmentToolbar from './EncashmentToolbar';
import ManageDisburse from './ManageDisburse';
import {
  employeeColumns,
  isRecordEditable,
  leaveColumns,
} from './encashment.helpers';

const RECORD_STATUS_CONFIG: StatusOption[] = [
  {
    label: 'Disbursed',
    value: 'DISBURSED',
    className: 'status status-success',
  },
  { label: 'Pending', value: 'PENDING', className: 'status status-yellow' },
  { label: 'Unpaid', value: 'UNPAID', className: 'status status-orange' },
  { label: 'Failed', value: 'FAILED', className: 'status status-error' },
];

/**
 * One encashment batch: every employee's encashment per leave type. Pending
 * records can be corrected, then the batch is disbursed into a payroll month.
 */
export default function BatchEncashments() {
  const { id = '' } = useParams();
  const [drawer, openDrawer, closeDrawer] = useDiscloser<
    'EDIT' | 'DISBURSE',
    EncashmentRecord
  >();

  const {
    skip,
    page,
    limit,
    filters,
    keyword,
    handleSearch,
    handleFilter,
    handlePagination,
  } = useAppFilters<{ office_id?: string }>(
    `${FILTER_KEYS.PAYROLL_LEAVE}-${id}`,
  );

  const { data, isLoading, isFetching } = useGetAllEncashmentByBatchQuery(
    {
      id,
      args: {
        params: { office_id: filters.office_id, search: keyword, skip, limit },
      },
    },
    { skip: !id },
  );
  const batch = data?.data;
  const records = batch?.records || [];
  const canDisburse = batch?.status === 'GENERATED';

  const columns: TableProps<EncashmentRecord>['columns'] = [
    ...(employeeColumns || []),
    ...(leaveColumns(records) || []),
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string) => (
        <StatusTag value={status} config={RECORD_STATUS_CONFIG} />
      ),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      align: 'center',
      render: (val?: string | null) => val || 'n/a',
    },
    {
      title: 'Actions',
      align: 'end',
      render: (_, record) =>
        isRecordEditable(record) ? (
          <Tooltip title="Edit encashment">
            <Button
              size="small"
              className="action-btn"
              icon={<PencilEdit02Icon size={20} />}
              onClick={() => openDrawer({ type: 'EDIT', data: record })}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Only pending records can be edited">
            <span className="inline-flex p-1 text-white/40">
              <LockIcon size={20} />
            </span>
          </Tooltip>
        ),
    },
  ];

  return (
    <DashboardLayout title="Employee">
      <PayrollPageHeader
        crumbs={[
          { title: 'Leave Encashment', path: PAYROLL_PATH },
          { title: 'Encashment List' },
        ]}
      >
        <EncashmentToolbar
          batch={batch}
          searchPlaceholder="Search Employee ID, Name"
          onSearch={handleSearch}
          officeId={filters.office_id}
          onOfficeChange={(value) => handleFilter('office_id', value)}
        />
      </PayrollPageHeader>

      <div className="mt-6">
        <CustomTable<EncashmentRecord>
          page={page}
          limit={limit}
          hideRowSelect
          columns={columns}
          dataSource={records}
          onPaginate={handlePagination}
          tableRootClassName="!bg-transparent !border-none !p-0"
          customTitle={batch?.batch_name || 'Encashment Batch'}
          loading={isLoading || isFetching}
          total={batch?.records_meta?.total || 0}
          filterSection={
            <>
              <TotalAmount value={batch?.total_amount} />
              <Tooltip
                title={
                  canDisburse ? '' : 'Only a generated batch can be disbursed'
                }
              >
                <Button
                  size="middle"
                  disabled={!canDisburse}
                  onClick={() =>
                    canDisburse && openDrawer({ type: 'DISBURSE' })
                  }
                  className="btn-primary !font-semibold !rounded-md"
                >
                  Disburse
                </Button>
              </Tooltip>
            </>
          }
        />
      </div>

      <EditEncashmentRecord
        open={
          drawer.open && drawer.type === 'EDIT' && isRecordEditable(drawer.data)
        }
        batchId={id}
        record={drawer.data}
        onClose={() => closeDrawer()}
      />

      <ManageDisburse
        open={drawer.open && drawer.type === 'DISBURSE' && canDisburse}
        batchId={id}
        handleClose={() => closeDrawer()}
      />
    </DashboardLayout>
  );
}
